import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Icon,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FiActivity, FiCheckCircle, FiCreditCard, FiDatabase, FiFile, FiFileText, FiLayers, FiMapPin, FiPackage, FiRefreshCw, FiTruck } from "react-icons/fi";
import WithSubnavigation from "./navbar";
import { useCookies } from "react-cookie";
import { useNavigate, useLocation } from "react-router-dom";

import { API_BASE, EASYSOL_API_BASE } from "../../config";
import { performLogout } from "../../utils/logout";
const EASYSOL_PROVIDER = "access";
const DELIVERY_API_PROVIDER = "borzo";

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
  { label: "All", value: "all" },
];

const PAYMENT_OPTIONS = [
  { label: "All Payments", value: "all" },
  { label: "Cash", value: "cash" },
  { label: "Credit", value: "credit" },
  { label: "Wallet", value: "wallet" },
  { label: "Prescription", value: "prescription" },
];

const TYPE_OPTIONS = [
  { label: "All Orders", value: "all" },
  { label: "Prescription", value: "prescription" },
  { label: "Regular", value: "regular" },
];

const statusColorMap = {
  Processing: "orange",
  Delivered: "green",
  Cancelled: "red",
};

const PAYMENT_PLAN_OPTIONS = [
  { label: "Cash on delivery", value: "cash", collectionMode: "delivery_cod" },
  { label: "UPI collected by pharmacy", value: "upi", collectionMode: "prepaid" },
  { label: "Card collected by pharmacy", value: "card", collectionMode: "prepaid" },
  { label: "Wallet settlement", value: "wallet", collectionMode: "prepaid" },
  { label: "Credit / online payment", value: "credit", collectionMode: "prepaid" },
];

const normalizeText = (value) => String(value || "").trim().toLowerCase();
const formatCurrency = (value) => `Rs ${Number(value || 0).toFixed(2)}`;
const clampDiscount = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(100, Math.max(0, parsed));
};

const clampMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

const getRequestedItemCode = (item) => {
  if (item?.itemCode) return String(item.itemCode);
  const productId = String(item?.productId || "");
  const easySolMatch = productId.match(/^EASYSOL(\d+)$/i);
  if (easySolMatch) return easySolMatch[1];
  if (/^\d+$/.test(productId)) return productId;
  return "";
};

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .some((entry) => entry.startsWith(`${name}=`));
};

const chooseBestMedicineMatch = (requestedName, matches) => {
  if (!Array.isArray(matches) || matches.length === 0) return null;
  const normalizedName = normalizeText(requestedName);
  return (
    matches.find((candidate) => normalizeText(candidate.item_name) === normalizedName) ||
    matches.find((candidate) => normalizeText(candidate.item_name).includes(normalizedName)) ||
    matches[0]
  );
};

function PharmacistOrders() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies, , removeCookie] = useCookies(["token", "role"]);
  const [username, setUsername] = useState("");
  const [fullUser, setFullUser] = useState({});
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [inventoryByOrder, setInventoryByOrder] = useState({});
  const [inventoryLoadingByOrder, setInventoryLoadingByOrder] = useState({});
  const [inventoryErrorByOrder, setInventoryErrorByOrder] = useState({});
  const [fulfillmentDraftByOrder, setFulfillmentDraftByOrder] = useState({});
  const [savingByOrder, setSavingByOrder] = useState({});
  const [postingByOrder, setPostingByOrder] = useState({});
  const [paymentSavingByOrder, setPaymentSavingByOrder] = useState({});
  const [deliveryQuoteByOrder, setDeliveryQuoteByOrder] = useState({});
  const [deliveryCreateByOrder, setDeliveryCreateByOrder] = useState({});
  const [markDeliveredByOrder, setMarkDeliveredByOrder] = useState({});
  const [deliveryRefreshByOrder, setDeliveryRefreshByOrder] = useState({});
  const [confirmingByOrder, setConfirmingByOrder] = useState({});
  const [proposingByOrder, setProposingByOrder] = useState({});
  const [rejectingByOrder, setRejectingByOrder] = useState({});
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      status: params.get("status") || "pending",
      paymentMethod: params.get("paymentMethod") || "all",
      orderType: params.get("orderType") || "all",
      search: params.get("search") || "",
    };
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilters((prev) => ({
      ...prev,
      status: params.get("status") || "pending",
      paymentMethod: params.get("paymentMethod") || "all",
      orderType: params.get("orderType") || "all",
      search: params.get("search") || "",
    }));
  }, [location.search]);
  const [medicineSearchResultsByItem, setMedicineSearchResultsByItem] = useState({});
  const [medicineSearchingByItem, setMedicineSearchingByItem] = useState({});
  const [selectedMedicineNameByItem, setSelectedMedicineNameByItem] = useState({});
  const [selectedMedicineDetailsByItem, setSelectedMedicineDetailsByItem] = useState({}); // Stores full medicine obj (manufacturer, salt, etc.)
  const [medicineBatchesByItem, setMedicineBatchesByItem] = useState({});
  const [selectedBatchByItem, setSelectedBatchByItem] = useState({});
  const [batchDropdownPositionByItem, setBatchDropdownPositionByItem] = useState({});
  const [manualBatchByItem, setManualBatchByItem] = useState({}); // For free-flow batch entry when EasySol unavailable
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [providerHealth, setProviderHealth] = useState({ easysol: null, marg: null }); // null = checking, true = healthy, false = down
  const inputRefs = useRef({});
  const batchInputRefs = useRef({});
  const toast = useToast();

  useEffect(() => {
    const verifyCookie = async () => {
      const hasToken = Boolean(cookies.token || hasCookie("token"));
      if (!hasToken) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const { data } = await axios.post(
          API_BASE,
          {},
          { withCredentials: true }
        );

        if (!data?.status) {
          removeCookie("token", { path: "/" });
          navigate("/", { replace: true });
          return;
        }

        setUsername(data.user);
      } catch (error) {
        removeCookie("token", { path: "/" });
        navigate("/", { replace: true });
      }
    };

    verifyCookie();
  }, [cookies.token, navigate, removeCookie]);

  useEffect(() => {
    const fetchFullUser = async () => {
      if (!username) return;
      try {
        const { data } = await axios.get(`${API_BASE}/pharmacist/myInfo/${username}`);
        setFullUser(data || {});
      } catch (error) {
        setFullUser({});
      }
    };

    fetchFullUser();
  }, [username]);

  // Track active dropdown itemKey for close-on-blur
  const [activeDropdownItemKey, setActiveDropdownItemKey] = useState(null);
  const dropdownCloseTimer = useRef(null);

  // Reposition dropdown on scroll/resize so it stays aligned with the input
  useEffect(() => {
    const reposition = () => {
      if (!activeDropdownItemKey) return;
      const el = inputRefs.current[activeDropdownItemKey];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 320),
      });
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [activeDropdownItemKey]);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/order/pharmacist/list`, {
          params: filters,
          withCredentials: true,
        });
        setOrders(data?.orders || []);
      } catch (error) {
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [filters]);

  // Initialize selected medicine and batch from pharmacistFulfillment when orders load
  useEffect(() => {
    if (!orders.length) return;
    
    const medicineNames = {};
    const medicineDetails = {};
    const batches = {};
    
    orders.forEach((order) => {
      // Skip pre-fill only for brand new orders that the pharmacist hasn't processed yet
      // For all other stages (sent for review, payment pending, confirmed, etc.) show saved selections
      const FRESH_STAGES = new Set(['draft', 'review']);
      if (!order.workflowStage || FRESH_STAGES.has(order.workflowStage)) return;
      
      (order.pharmacistFulfillment?.lines || []).forEach((line) => {
        const itemKey = line.requestedItemKey;
        if (!itemKey) return;
        const scopedKey = `${order._id}::${itemKey}`;
        
        // Get selected medicine from allocations or line
        const allocation = line.allocations?.[0];
        // Try allocation first, then fall back to line.itemName
        const medName = allocation?.itemName || line.itemName || "";
        if (medName) {
          medicineNames[scopedKey] = medName;
          medicineDetails[scopedKey] = {
            item_name: medName,
            manufacturer: allocation?.manufacturer || "",
            pack_size: allocation?.packSize || "",
          };
        }
        
        // Get selected batch
        if (allocation?.batchNo) {
          batches[scopedKey] = {
            batch_no: allocation.batchNo,
            expiry: allocation.expiry,
            available_qty: allocation.quantity,
          };
        }
      });
    });
    
    if (Object.keys(medicineNames).length > 0) {
      setSelectedMedicineNameByItem((prev) => ({ ...prev, ...medicineNames }));
      setSelectedMedicineDetailsByItem((prev) => ({ ...prev, ...medicineDetails }));
    }
    if (Object.keys(batches).length > 0) {
      setSelectedBatchByItem((prev) => ({ ...prev, ...batches }));
    }
  }, [orders]);

  const Logout = () => performLogout(removeCookie);

  useEffect(() => {
    if (!expandedOrderId && orders.length) {
      setExpandedOrderId(orders[0]._id);
    }
  }, [expandedOrderId, orders]);

  // Initialize drafts for all orders to ensure all items are present
  useEffect(() => {
    if (orders.length > 0) {
      setFulfillmentDraftByOrder((prev) => {
        const updated = { ...prev };
        orders.forEach((order) => {
          if (order._id && !updated[order._id]) {
            updated[order._id] = buildInitialDraft(order);
          }
        });
        return updated;
      });
    }
  }, [orders]);

  const isPharmacyConnected = (order) => {
    return order?.pharmacy?.inventory?.type && order.pharmacy.inventory.type !== 'offline';
  };

  const isProviderDown = (order) => {
    const inventoryType = order?.pharmacy?.inventory?.type;
    if (inventoryType === 'easysol' && providerHealth.easysol === false) return true;
    if (inventoryType === 'marg' && providerHealth.marg === false) return true;
    return false;
  };

  useEffect(() => {
    const checkProviderHealth = async () => {
      try {
        await axios.get(`${EASYSOL_API_BASE}/integration/easysol/health?provider=${EASYSOL_PROVIDER}`, { timeout: 3000 });
        setProviderHealth((prev) => ({ ...prev, easysol: true }));
      } catch (error) {
        setProviderHealth((prev) => ({ ...prev, easysol: false }));
      }
    };
    checkProviderHealth();
  }, []);

  const refreshOrders = async () => {
    const { data } = await axios.get(`${API_BASE}/order/pharmacist/list`, {
      params: filters,
      withCredentials: true,
    });
    setOrders(data?.orders || []);
  };

  const totalItems = useMemo(
    () => orders.reduce((sum, order) => sum + (order.items || []).reduce((itemSum, item) => itemSum + (item.quantity || 0), 0), 0),
    [orders]
  );

  const getFileUrl = (filePath) => {
    if (!filePath) return "";
    if (/^https?:\/\//i.test(String(filePath))) return filePath; // already absolute (S3 URL)
    const normalizedPath = String(filePath).replace(/^\/+/, "");
    return `${API_BASE}/${normalizedPath}`;
  };

  const isImageFile = (mimeType) => String(mimeType || "").startsWith("image/");

  const formatStatus = (status) => (status === "Processing" ? "Pending" : status);

  // Order is only editable in 'review' stage (not after confirm/send for review)
  const isOrderEditable = (order) => order?.workflowStage === 'review';

  // Validation: Check all items have local medicine + batch OR are marked out of stock
  const validateFulfillmentComplete = (order) => {
    if (!isPharmacyConnected(order)) return { valid: true }; // Not using EasySol, no validation needed
    
    const draft = getOrderDraft(order);
    const items = order.items || [];
    const errors = [];
    
    items.forEach((item, index) => {
      const itemKey = getRequestedItemKey(item, index);
      const line = draft.lines?.[itemKey];
      
      // If out of stock, it's valid
      if (line?.isOutOfStock) return;
      
      // Check if local medicine is selected
      if (!line?.itemCode) {
        errors.push(`"${item.name || item.product_name || 'Item ' + (index + 1)}": Local medicine not selected`);
        return;
      }
      
      // Check if batch is selected
      if (!line?.allocations || line.allocations.length === 0) {
        errors.push(`"${item.name || item.product_name || 'Item ' + (index + 1)}": Batch not selected`);
      }
    });
    
    return {
      valid: errors.length === 0,
      errors,
      missingCount: errors.length,
    };
  };

  const getRequestedItemKey = (item, index) =>
    `${getRequestedItemCode(item) || item?.name || "item"}-${index}`;

  const getScopedKey = (orderId, itemKey) => `${orderId}::${itemKey}`;

  const buildInitialDraft = (order) => {
    const persistedLines = order.pharmacistFulfillment?.lines?.reduce((acc, line) => {
      acc[line.requestedItemKey] = {
        requestedItemKey: line.requestedItemKey,
        itemCode: line.itemCode,
        itemName: line.itemName,
        requestedQty: Number(line.requestedQty || 0),
        discountPercent: Number(line.discountPercent || 0),
        mrpOverride: Number(line.mrpOverride || 0),
        isOutOfStock: Boolean(line.isOutOfStock),
        allocations: (line.allocations || []).map((allocation) => ({
          batchNo: allocation.batchNo,
          quantity: Number(allocation.quantity || 0),
          saleRate: Number(allocation.saleRate || 0),
          mrp: Number(allocation.mrp || 0),
          expiry: allocation.expiry || "",
          availableQty: Number(allocation.availableQty || 0),
        })),
      };
      return acc;
    }, {}) || {};

    const allLines = { ...persistedLines };
    (order.items || []).forEach((item, index) => {
      const itemKey = getRequestedItemKey(item, index);
      if (!allLines[itemKey]) {
        allLines[itemKey] = {
          requestedItemKey: itemKey,
          itemCode: item.itemCode || "",
          itemName: item.name || "",
          requestedQty: Number(item.quantity || 1),
          discountPercent: 0,
          mrpOverride: Number(item.price || 0),
          isOutOfStock: false,
          allocations: [],
        };
      }
    });

    // Restore isOutOfStock from pharmacistFulfillment lines if they exist
    (order.pharmacistFulfillment?.lines || []).forEach((line) => {
      const itemKey = line.requestedItemKey;
      if (itemKey && allLines[itemKey]) {
        allLines[itemKey].isOutOfStock = line.isOutOfStock || false;
      }
    });

    return {
      grossDiscountPercent: Number(order.pharmacistFulfillment?.grossDiscountPercent || 0),
      lines: allLines,
    };
  };

  const getOrderDraft = (order) => fulfillmentDraftByOrder[order._id] || buildInitialDraft(order);

  const getDraftLines = (order) => getOrderDraft(order).lines || {};

  const getLineDraft = (order, itemKey) => getDraftLines(order)[itemKey];

  const getPaymentPlan = (order) => {
    const persistedMethod = order.paymentWorkflow?.selectedMethod;
    const selectedMethod = PAYMENT_PLAN_OPTIONS.some((option) => option.value === persistedMethod)
      ? persistedMethod
      : "cash";
    const option = PAYMENT_PLAN_OPTIONS.find((candidate) => candidate.value === selectedMethod);
    return {
      selectedMethod,
      collectionMode: order.paymentWorkflow?.collectionMode || option?.collectionMode || "delivery_cod",
      notes: order.paymentWorkflow?.notes || "",
      payableAmount: clampMoney(
        order.paymentWorkflow?.isFinalized
          ? order.paymentWorkflow?.payableAmount
          : getFinalTotal(order) || order.bill || 0
      ),
      isFinalized: Boolean(order.paymentWorkflow?.isFinalized),
    };
  };

  const getLineTotals = (line, order, itemKey) => {
    const mrp = Number(line?.mrpOverride || 0);
    const qty = Number(line?.requestedQty || 0);
    const subtotal = mrp * qty;
    const discountPercent = clampDiscount(line?.discountPercent || 0);
    const discountAmount = subtotal * (discountPercent / 100);
    return {
      subtotal,
      discountPercent,
      discountAmount,
      netAmount: subtotal - discountAmount,
      allocatedQty: qty,
    };
  };

  const getOrderTotals = (order) => {
    const draft = getOrderDraft(order);
    const items = order.items || [];
    const lineTotals = Object.values(draft.lines || {})
      .filter((line) => !line.isOutOfStock)
      .map((line) => {
        const item = items.find((i) => {
          const itemKey = getRequestedItemKey(i, items.indexOf(i));
          return itemKey === line.requestedItemKey;
        });
        const lineWithQty = {
          ...line,
          requestedQty: line.requestedQty || item?.quantity || 0,
        };
        return getLineTotals(lineWithQty, order, line.requestedItemKey);
      });
    const subtotal = lineTotals.reduce((sum, line) => sum + line.subtotal, 0);
    const lineDiscountTotal = lineTotals.reduce((sum, line) => sum + line.discountAmount, 0);
    const afterLineDiscount = subtotal - lineDiscountTotal;
    const grossDiscountPercent = clampDiscount(draft.grossDiscountPercent || 0);
    const grossDiscountAmount = afterLineDiscount * (grossDiscountPercent / 100);
    return {
      subtotal,
      lineDiscountTotal,
      afterLineDiscount,
      grossDiscountPercent,
      grossDiscountAmount,
      netAmount: afterLineDiscount - grossDiscountAmount,
    };
  };

  const getDeliveryFee = (order) =>
    Number(
      order?.deliveryWorkflow?.selectedOption?.deliveryFee ||
      order?.deliveryWorkflow?.feeAmount ||
      0
    );

  const getFinalTotal = (order) => {
    const medicineTotal = getOrderTotals(order).netAmount;
    const deliveryFee = getDeliveryFee(order);
    return Number((medicineTotal + deliveryFee).toFixed(2));
  };

  const setOrderDraft = (orderId, updater) => {
    setFulfillmentDraftByOrder((prev) => {
      const current = prev[orderId] || { grossDiscountPercent: 0, lines: {} };
      return {
        ...prev,
        [orderId]: typeof updater === "function" ? updater(current) : updater,
      };
    });
  };

  const toggleBatchAllocation = (orderId, itemKey, itemMeta, batch, enabled) => {
    setOrderDraft(orderId, (current) => {
      const existingLine = current[itemKey] || {
        requestedItemKey: itemKey,
        itemCode: itemMeta.itemCode,
        itemName: itemMeta.itemName,
        requestedQty: itemMeta.requestedQty,
        discountPercent: 0,
        allocations: [],
      };

      const withoutBatch = (existingLine.allocations || []).filter(
        (allocation) => allocation.batchNo !== batch.batch_no
      );

      const nextAllocations = enabled
        ? [
            ...withoutBatch,
            {
              batchNo: batch.batch_no,
              quantity: Math.min(itemMeta.requestedQty, Number(batch.available_qty || 0)),
              saleRate: Number(batch.sale_rate || 0),
              mrp: Number(batch.mrp || 0),
              expiry: batch.expiry || "",
              availableQty: Number(batch.available_qty || 0),
            },
          ]
        : withoutBatch;

      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...existingLine,
            allocations: nextAllocations,
          },
        },
      };
    });
  };

  const updateBatchAllocationQty = (orderId, itemKey, batchNo, value) => {
    const parsed = Math.max(0, Number(value || 0));
    setOrderDraft(orderId, (current) => {
      const line = current.lines?.[itemKey];
      if (!line) return current;
      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...line,
            allocations: (line.allocations || []).map((allocation) =>
              allocation.batchNo === batchNo
                ? {
                    ...allocation,
                    quantity: Math.min(parsed, allocation.availableQty || parsed),
                  }
                : allocation
            ),
          },
        },
      };
    });
  };

  const updateLineDiscount = (orderId, itemKey, value, order) => {
    setOrderDraft(orderId, (current) => {
      let line = current.lines?.[itemKey];
      if (!line && order?.items) {
        const item = order.items.find((i, idx) => getRequestedItemKey(i, idx) === itemKey);
        line = {
          requestedItemKey: itemKey,
          itemCode: item?.itemCode || "",
          itemName: item?.name || "",
          requestedQty: Number(item?.quantity || 1),
          discountPercent: 0,
          mrpOverride: Number(item?.price || 0),
          allocations: [],
        };
      }
      if (!line) return current;
      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...line,
            discountPercent: clampDiscount(value),
          },
        },
      };
    });
  };

  const updateLineMrpOverride = (orderId, itemKey, value, order) => {
    setOrderDraft(orderId, (current) => {
      let line = current.lines?.[itemKey];
      if (!line && order?.items) {
        const item = order.items.find((i, idx) => getRequestedItemKey(i, idx) === itemKey);
        line = {
          requestedItemKey: itemKey,
          itemCode: item?.itemCode || "",
          itemName: item?.name || "",
          requestedQty: Number(item?.quantity || 1),
          discountPercent: 0,
          mrpOverride: Number(item?.price || 0),
          allocations: [],
        };
      }
      if (!line) return current;
      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...line,
            mrpOverride: clampMoney(value),
          },
        },
      };
    });
  };

  const toggleLineOutOfStock = (orderId, itemKey) => {
    setOrderDraft(orderId, (current) => {
      let line = current.lines?.[itemKey];
      if (!line) {
        line = {
          requestedItemKey: itemKey,
          itemCode: "",
          itemName: "",
          requestedQty: 0,
          discountPercent: 0,
          mrpOverride: 0,
          allocations: [],
        };
      }
      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...line,
            isOutOfStock: !line.isOutOfStock,
            allocations: !line.isOutOfStock ? [] : line.allocations,
          },
        },
      };
    });
  };

  const updateGrossDiscount = (orderId, value) => {
    setOrderDraft(orderId, (current) => ({
      ...current,
      grossDiscountPercent: clampDiscount(value),
    }));
  };

  const saveFulfillment = async (order) => {
    const draft = getOrderDraft(order);
    setSavingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      await axios.post(
        `${API_BASE}/order/${order._id}/fulfillment`,
        {
          provider: EASYSOL_PROVIDER,
          grossDiscountPercent: draft.grossDiscountPercent || 0,
          fulfillmentLines: Object.values(draft.lines || {}).map(line => ({
            ...line,
            mrpOverride: line.mrpOverride || 0,
            isOutOfStock: line.isOutOfStock || false,
          })),
        },
        { withCredentials: true }
      );
      toast({
        title: "Fulfillment saved",
        description: "Selected batches, MRP, and discounts were saved to this order.",
        status: "success",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not save fulfillment",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setSavingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const savePaymentPlan = async (order) => {
    const paymentPlan = getPaymentPlan(order);
    setPaymentSavingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      await axios.post(
        `${API_BASE}/order/${order._id}/payment-plan`,
        {
          selectedMethod: paymentPlan.selectedMethod,
          collectionMode: paymentPlan.collectionMode,
          payableAmount: paymentPlan.payableAmount,
          notes: paymentPlan.notes,
          isFinalized: true,
        },
        { withCredentials: true }
      );
      await refreshOrders();
      toast({
        title: "Payment plan saved",
        description: "Payment mode and payable amount are now attached to this order.",
        status: "success",
        duration: 2400,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not save payment plan",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setPaymentSavingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const callEasysolDirect = async (order) => {
    const draft = getOrderDraft(order);
    const lines = Object.values(draft.lines || {});
    const salesItems = lines.flatMap(line =>
      (line.allocations || []).map(allocation => ({
        item_code: line.itemCode,
        batch_no: allocation.batchNo,
        quantity: Number(allocation.quantity).toFixed(2),
        discount_percent: Number(line.discountPercent || 0).toFixed(2),
      }))
    );
    const resp = await axios.post(
      `${EASYSOL_API_BASE}/sales/orders/direct?provider=${encodeURIComponent(EASYSOL_PROVIDER)}`,
      {
        items: salesItems,
        gross_discount_percent: Number(draft.grossDiscountPercent || 0).toFixed(2),
        customer_name: order.userId?.toString() || 'Customer',
        customer_mobile: order.address?.mobile || '',
        narration: `Order ${order._id}`,
        payment_mode: 'CASH',
      }
    );
    return resp.data;
  };

  const postToEasySol = async (order) => {
    const draft = getOrderDraft(order);
    setPostingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      // Call EasySol directly from pharmacist's browser (localhost)
      const easysolResult = await callEasysolDirect(order);

      const { data } = await axios.post(
        `${API_BASE}/order/${order._id}/post-easysol`,
        {
          provider: EASYSOL_PROVIDER,
          grossDiscountPercent: draft.grossDiscountPercent || 0,
          fulfillmentLines: Object.values(draft.lines || {}).map(line => ({
            ...line,
            mrpOverride: line.mrpOverride || 0,
            isOutOfStock: line.isOutOfStock || false,
          })),
          easysolResult,
        },
        { withCredentials: true }
      );
      toast({
        title: "Posted to EasySol",
        description: data?.easysolPosting?.invoiceNo
          ? `Invoice ${data.easysolPosting.invoiceNo} created successfully.`
          : "Order posted successfully.",
        status: "success",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
      await refreshOrders();
    } catch (error) {
      toast({
        title: "EasySol posting failed",
        description: error?.response?.data?.error || error?.response?.data?.message || "Please review the batch selection and try again.",
        status: "error",
        duration: 3200,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setPostingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const quoteDelivery = async (order) => {
    setDeliveryQuoteByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      const { data } = await axios.post(
        `${API_BASE}/order/${order._id}/delivery/quote`,
        {},
        { withCredentials: true }
      );
      await refreshOrders();
      toast({
        title: "Delivery quote ready",
        description: data?.deliveryWorkflow?.quote?.deliveryFee
          ? `Estimated fee ${formatCurrency(data.deliveryWorkflow.quote.deliveryFee)}`
          : "Delivery quote refreshed.",
        status: "success",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not quote delivery",
        description: error?.response?.data?.message || error?.response?.data?.detail || "Borzo may not be configured yet.",
        status: "warning",
        duration: 3200,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setDeliveryQuoteByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const createDelivery = async (order) => {
    setDeliveryCreateByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      // Check if user selected a third-party delivery option
      const selectedOption = order.deliveryWorkflow?.selectedOption;
      const useThirdPartyDelivery = selectedOption && selectedOption.id;

      if (useThirdPartyDelivery) {
        // Create delivery with Borzo
        const { data } = await axios.post(
          `${API_BASE}/order/${order._id}/delivery/create`,
          {
            provider: DELIVERY_API_PROVIDER,
            selectedOption: selectedOption,
            notes: order.deliveryWorkflow?.notes || "",
          },
          { withCredentials: true }
        );
        await refreshOrders();
        toast({
          title: "Delivery dispatched",
          description: data?.deliveryWorkflow?.borzoOrderId
            ? `Borzo order ${data.deliveryWorkflow.borzoOrderId} created.`
            : "Delivery order created.",
          status: "success",
          duration: 2600,
          isClosable: true,
          position: "bottom-right",
        });
      } else {
        // Pharmacy handles its own delivery
        await axios.post(
          `${API_BASE}/order/${order._id}/delivery/create`,
          {},
          { withCredentials: true }
        );
        await refreshOrders();
        toast({
          title: "Delivery confirmed",
          description: "Pharmacy will handle delivery.",
          status: "success",
          duration: 2600,
          isClosable: true,
          position: "bottom-right",
        });
      }
    } catch (error) {
      toast({
        title: "Could not start delivery",
        description: error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.detail || "Please try again.",
        status: "error",
        duration: 3200,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setDeliveryCreateByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const markOrderDelivered = async (order) => {
    setMarkDeliveredByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      await axios.post(
        `${API_BASE}/order/${order._id}/delivery/mark-delivered`,
        {},
        { withCredentials: true }
      );
      await refreshOrders();
      toast({
        title: "Order marked as delivered",
        status: "success",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not mark as delivered",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 3200,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setMarkDeliveredByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const generateInvoice = async (order) => {
    if (order.status !== 'Confirmed') {
      toast({
        title: "Order not confirmed",
        description: "Please confirm the order before generating invoice.",
        status: "warning",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }

    setPostingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      // Call EasySol directly from pharmacist's browser (localhost), then pass result to backend
      const easysolResult = await callEasysolDirect(order);

      const { data } = await axios.post(
        `${API_BASE}/order/${order._id}/generate-invoice`,
        { easysolResult },
        { withCredentials: true }
      );

      await refreshOrders();
      toast({
        title: "Invoice generated",
        description: data?.easysolPosting?.invoiceNo
          ? `Invoice ${data.easysolPosting.invoiceNo} created successfully.`
          : "Invoice generated successfully.",
        status: "success",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      const detail = error?.response?.data?.message || error?.response?.data?.error || "Please try again.";
      toast({
        title: "Could not generate invoice",
        description: detail,
        status: "error",
        duration: 3200,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setPostingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const confirmOrder = async (order) => {
    setConfirmingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      const draft = getOrderDraft(order);
      await axios.post(
        `${API_BASE}/order/${order._id}/confirm`,
        {
          provider: EASYSOL_PROVIDER,
          grossDiscountPercent: draft.grossDiscountPercent || 0,
          fulfillmentLines: Object.values(draft.lines || {}).map(line => ({
            ...line,
            mrpOverride: line.mrpOverride || 0,
            isOutOfStock: line.isOutOfStock || false,
          })),
        },
        { withCredentials: true }
      );
      await refreshOrders();
      toast({
        title: "Order confirmed",
        description: "Fulfillment saved. Ready to generate invoice.",
        status: "success",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not confirm order",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setConfirmingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const pharmacistSendForReview = async (order) => {
    // Validate all medicines are fulfilled or marked out of stock
    const validation = validateFulfillmentComplete(order);
    if (!validation.valid) {
      toast({
        title: `Cannot send for review: ${validation.missingCount} item(s) incomplete`,
        description: validation.errors.slice(0, 3).join('; ') + (validation.errors.length > 3 ? ` and ${validation.errors.length - 3} more...` : ''),
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    // Check if all medicines are marked as out of stock (nothing to send for review)
    const draft = getOrderDraft(order);
    const items = order.items || [];
    const allOutOfStock = items.length > 0 && items.every((item, idx) => {
      const itemKey = getRequestedItemKey(item, idx);
      const line = draft.lines?.[itemKey];
      return line?.isOutOfStock === true;
    });
    if (allOutOfStock) {
      toast({
        title: "Cannot send for review",
        description: "All medicines are marked as out of stock. Please reject the order instead.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "bottom-right",
      });
      return;
    }
    
    setProposingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      const lines = Object.values(draft.lines || {});
      const outOfStockLines = lines.filter(line => line.isOutOfStock);
      const priceChangedLines = lines.filter(line => {
        if (line.isOutOfStock) return false;
        const originalItem = (order.items || []).find(
          item => item.itemCode === line.itemCode || item.name === line.itemName
        );
        const originalPrice = Number(originalItem?.price || 0);
        // Prefer pharmacist-configured MRP/discount over batch sale rate
        const saleRate = line.allocations?.length ? Number(line.allocations[0].saleRate || 0) : 0;
        const mrp = Number(line.mrpOverride || 0);
        const discount = Number(line.discountPercent || 0);
        const effectivePrice = mrp > 0
          ? Number((mrp * (1 - discount / 100)).toFixed(2))
          : saleRate > 0
            ? saleRate
            : originalPrice;
        return effectivePrice > 0 && Math.abs(effectivePrice - originalPrice) > 0.01;
      });

      // If no out of stock or price changes, still send for review (customer needs to confirm)
      // The pharmacist has verified all items are available at the shown prices

      const unavailableChanges = outOfStockLines.map(line => {
        const originalItem = (order.items || []).find(
          item => item.itemCode === line.itemCode || item.name === line.itemName
        );
        return {
          changeType: 'unavailable',
          requestedItemKey: line.requestedItemKey || line.itemCode || line.itemName || '',
          requestedItemName: line.itemName || '',
          requestedQuantity: line.requestedQty || originalItem?.quantity || 0,
          requestedPrice: Number(originalItem?.price || 0),
          reason: 'Item is currently out of stock',
        };
      });

      const priceChanges = priceChangedLines.map(line => {
        const originalItem = (order.items || []).find(
          item => item.itemCode === line.itemCode || item.name === line.itemName
        );
        const originalPrice = Number(originalItem?.price || 0);
        // Prefer pharmacist-configured MRP/discount over batch sale rate
        const saleRate = line.allocations?.length ? Number(line.allocations[0].saleRate || 0) : 0;
        const mrp = Number(line.mrpOverride || 0);
        const discount = Number(line.discountPercent || 0);
        const effectivePrice = mrp > 0
          ? Number((mrp * (1 - discount / 100)).toFixed(2))
          : saleRate > 0
            ? saleRate
            : originalPrice;
        const hasCustomMrp = mrp > 0 && Math.abs(mrp - originalPrice) > 0.01;
        const reason = discount > 0
          ? `MRP ₹${mrp.toFixed(2)} with ${discount}% discount`
          : hasCustomMrp
            ? 'Price updated by pharmacist'
            : saleRate > 0
              ? `Price confirmed at ₹${saleRate.toFixed(2)} from pharmacy stock`
              : 'Price updated by pharmacist';
        return {
          changeType: 'price_change',
          requestedItemKey: line.requestedItemKey || line.itemCode || line.itemName || '',
          requestedItemName: line.itemName || '',
          requestedQuantity: line.requestedQty || originalItem?.quantity || 0,
          requestedPrice: originalPrice,
          newPrice: effectivePrice,
          reason,
        };
      });

      const changes = [...unavailableChanges, ...priceChanges];

      await axios.post(
        `${API_BASE}/order/${order._id}/pharmacist-propose-changes`,
        {
          changes,
          provider: EASYSOL_PROVIDER,
          grossDiscountPercent: draft.grossDiscountPercent || 0,
          fulfillmentLines: lines.map(line => ({
            ...line,
            mrpOverride: line.mrpOverride || 0,
            isOutOfStock: line.isOutOfStock || false,
          })),
        },
        { withCredentials: true }
      );
      await refreshOrders();
      const totalChanges = outOfStockLines.length + priceChangedLines.length;
      toast({
        title: totalChanges > 0 ? "Changes sent to customer" : "Order sent for review",
        description: totalChanges > 0
          ? `${totalChanges} change${totalChanges !== 1 ? 's' : ''} sent for review. Customer will be notified.`
          : "Order verified and sent for customer confirmation.",
        status: "success",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not send for review",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setProposingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const rejectOrder = async (order) => {
    setRejectingByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      await axios.post(`${API_BASE}/order/${order._id}/reject`, {}, { withCredentials: true });
      await refreshOrders();
      toast({
        title: "Order rejected",
        description: "Order has been cancelled.",
        status: "warning",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not reject order",
        description: error?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setRejectingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const refreshDeliveryStatus = async (order) => {
    setDeliveryRefreshByOrder((prev) => ({ ...prev, [order._id]: true }));
    try {
      await axios.post(
        `${API_BASE}/order/${order._id}/delivery/status`,
        {},
        { withCredentials: true }
      );
      await refreshOrders();
      toast({
        title: "Delivery status refreshed",
        status: "info",
        duration: 2200,
        isClosable: true,
        position: "bottom-right",
      });
    } catch (error) {
      toast({
        title: "Could not refresh delivery",
        description: error?.response?.data?.message || error?.response?.data?.detail || "Please try again.",
        status: "warning",
        duration: 2600,
        isClosable: true,
        position: "bottom-right",
      });
    } finally {
      setDeliveryRefreshByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  const searchMedicines = async (itemKey, query, inputElement, order) => {
    if (!isPharmacyConnected(order) || !query || query.length < 2) {
      setMedicineSearchResultsByItem((prev) => ({ ...prev, [itemKey]: [] }));
      setDropdownPosition(null);
      return;
    }
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 320),
      });
    }
    setMedicineSearchingByItem((prev) => ({ ...prev, [itemKey]: true }));
    try {
      const { data } = await axios.get(`${EASYSOL_API_BASE}/sales/medicines/search`, {
        params: { q: query, limit: 10, provider: EASYSOL_PROVIDER },
      });
      setMedicineSearchResultsByItem((prev) => ({
        ...prev,
        [itemKey]: Array.isArray(data) ? data : [],
      }));
    } catch (error) {
      setMedicineSearchResultsByItem((prev) => ({ ...prev, [itemKey]: [] }));
    } finally {
      setMedicineSearchingByItem((prev) => ({ ...prev, [itemKey]: false }));
    }
  };

  const selectMedicine = async (orderId, itemKey, medicine, order) => {
    const scopedKey = getScopedKey(orderId, itemKey);
    // Update the selected medicine name and details for display
    setSelectedMedicineNameByItem((prev) => ({
      ...prev,
      [scopedKey]: medicine.item_name,
    }));
    setSelectedMedicineDetailsByItem((prev) => ({
      ...prev,
      [scopedKey]: medicine, // Store full object: manufacturer, salt, pack_size, etc.
    }));
    // Update itemCode and MRP in the draft line with EasySol values
    setOrderDraft(orderId, (current) => {
      let line = current.lines?.[itemKey];
      if (!line && order?.items) {
        const item = order.items.find((i, idx) => getRequestedItemKey(i, idx) === itemKey);
        line = {
          requestedItemKey: itemKey,
          itemCode: item?.itemCode || "",
          itemName: item?.name || "",
          requestedQty: Number(item?.quantity || 1),
          discountPercent: 0,
          mrpOverride: Number(item?.price || 0),
          allocations: [],
        };
      }
      if (!line) return current;
      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...line,
            itemCode: String(medicine.item_code || line.itemCode || ""),
            itemName: medicine.item_name || line.itemName,
            mrpOverride: Number(medicine.mrp || line.mrpOverride || 0),
          },
        },
      };
    });
    // Clear search results and position
    setMedicineSearchResultsByItem((prev) => ({
      ...prev,
      [itemKey]: [],
    }));
    setDropdownPosition(null);
    // Fetch batches for the selected medicine
    if (medicine.item_code) {
      await fetchMedicineBatches(itemKey, medicine.item_code);
    }
  };

  const fetchMedicineBatches = async (itemKey, itemCode) => {
    try {
      const { data } = await axios.get(`${EASYSOL_API_BASE}/sales/medicines/${itemCode}/batches`, {
        params: { provider: EASYSOL_PROVIDER },
      });
      // API returns MedicineWithBatchesRead object with 'batches' array and 'unit'
      const batches = (data?.batches || []).map(b => ({...b, unit: data?.unit}));
      setMedicineBatchesByItem((prev) => ({
        ...prev,
        [itemKey]: batches,
      }));
    } catch (error) {
      console.error("Failed to fetch batches:", error);
      setMedicineBatchesByItem((prev) => ({
        ...prev,
        [itemKey]: [],
      }));
    }
  };

  const selectBatch = (orderId, itemKey, batch, order) => {
    const scopedKey = getScopedKey(orderId, itemKey);
    setSelectedBatchByItem((prev) => ({
      ...prev,
      [scopedKey]: batch,
    }));
    // Update the draft with batch info
    setOrderDraft(orderId, (current) => {
      let line = current.lines?.[itemKey];
      if (!line && order?.items) {
        const item = order.items.find((i, idx) => getRequestedItemKey(i, idx) === itemKey);
        line = {
          requestedItemKey: itemKey,
          itemCode: item?.itemCode || "",
          itemName: item?.name || "",
          requestedQty: Number(item?.quantity || 1),
          discountPercent: 0,
          mrpOverride: Number(item?.price || 0),
          allocations: [],
        };
      }
      if (!line) return current;
      return {
        ...current,
        lines: {
          ...(current.lines || {}),
          [itemKey]: {
            ...line,
            batchNo: batch.batch_no,
            batchExpiry: batch.expiry,
            batchMRP: batch.mrp,
            // Update MRP override with batch sale_rate (price)
            mrpOverride: batch.sale_rate || batch.mrp,
            allocations: [{
              batchNo: batch.batch_no,
              quantity: line.requestedQty || 1,
              saleRate: batch.sale_rate || batch.mrp,
              mrp: batch.mrp,
              expiry: batch.expiry,
              availableQty: batch.available_qty,
              unit: batch.unit,
            }],
          },
        },
      };
    });
    // Clear batch dropdown position for this item
    setBatchDropdownPositionByItem((prev) => ({
      ...prev,
      [itemKey]: null,
    }));
  };

  const loadInventoryForOrder = async (order) => {
    if (!order?._id) return;
    setInventoryLoadingByOrder((prev) => ({ ...prev, [order._id]: true }));
    setInventoryErrorByOrder((prev) => ({ ...prev, [order._id]: "" }));

    try {
      const requestedItems = order.items || [];
      const inventoryCards = await Promise.all(
        requestedItems.map(async (requestedItem) => {
          let itemCode = getRequestedItemCode(requestedItem);
          let catalog = null;
          let stock = null;
          let batches = [];

          if (itemCode) {
            try {
              const catalogResp = await axios.get(`${EASYSOL_API_BASE}/sales/medicines/search`, {
                params: { q: itemCode, limit: 5, provider: EASYSOL_PROVIDER },
              });
              catalog = chooseBestMedicineMatch(requestedItem.name, catalogResp.data);
            } catch (error) {
              catalog = null;
            }
          }

          if (!itemCode) {
            const searchResp = await axios.get(`${EASYSOL_API_BASE}/sales/medicines/search`, {
              params: { q: requestedItem.name, limit: 5, provider: EASYSOL_PROVIDER },
            });
            catalog = chooseBestMedicineMatch(requestedItem.name, searchResp.data);
            itemCode = catalog?.item_code || "";
          }

          if (itemCode) {
            try {
              const stockResp = await axios.get(`${EASYSOL_API_BASE}/medicines/${itemCode}/stock`, {
                params: { provider: EASYSOL_PROVIDER },
              });
              stock = stockResp.data;
            } catch (error) {
              stock = null;
            }

            try {
              const batchesResp = await axios.get(`${EASYSOL_API_BASE}/sales/medicines/${itemCode}/batches`, {
                params: { provider: EASYSOL_PROVIDER },
              });
              batches = batchesResp.data?.batches || [];
              if (!catalog) {
                catalog = batchesResp.data;
              }
            } catch (error) {
              batches = [];
            }
          }

          return {
            requestedItem,
            itemCode,
            catalog,
            stock,
            batches,
            matched: Boolean(itemCode && (catalog || stock)),
          };
        })
      );

      setInventoryByOrder((prev) => ({ ...prev, [order._id]: inventoryCards }));
    } catch (error) {
      setInventoryErrorByOrder((prev) => ({
        ...prev,
        [order._id]: "Could not load live EasySol inventory details.",
      }));
    } finally {
      setInventoryLoadingByOrder((prev) => ({ ...prev, [order._id]: false }));
    }
  };

  return (
    <>
      <div style={{ position: "fixed", top: "0", left: "0", right: "0", zIndex: "1000" }}>
        <WithSubnavigation
          username={username}
          name={fullUser.Name}
          isLoggedIn={!!cookies.token}
          openChangePasswordModal={() => {}}
          onLogout={Logout}
          notifications={fullUser.Notifications}
        />
      </div>

      <Box
        bg="#f5f7fb"
        minH="100vh"
        pt="96px"
        pb="32px"
        px={{ base: 4, md: 8 }}
      >
        <Stack spacing={6}>
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={4}
            bg="white"
            borderRadius="20px"
            p={{ base: 4, md: 6 }}
            boxShadow="0 12px 34px rgba(15, 23, 42, 0.08)"
          >
            <Box>
              <Text fontSize="xs" textTransform="uppercase" color="gray.500" letterSpacing="0.08em">
                Pharmacist Orders
              </Text>
              <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="700" color="#0f172a">
                {filters.status === "delivered"
                  ? "Delivered Orders"
                  : filters.status === "cancelled"
                    ? "Cancelled Orders"
                    : filters.status === "all"
                      ? "All Orders"
                      : "Pending Orders Queue"}
              </Text>
              <Text color="gray.600">
                {filters.status === "delivered"
                  ? "Completed orders successfully delivered"
                  : filters.status === "cancelled"
                    ? "Orders cancelled or rejected"
                    : filters.status === "all"
                      ? "All orders across all statuses"
                      : "All pending orders awaiting fulfillment"}
              </Text>
            </Box>
            <HStack spacing={3} wrap="wrap">
              <Box bg="#ecfeff" borderRadius="16px" px={4} py={3}>
                <Text fontSize="xs" color="gray.500">Orders</Text>
                <Text fontSize="xl" fontWeight="700">{orders.length}</Text>
              </Box>
              <Box bg="#f0fdf4" borderRadius="16px" px={4} py={3}>
                <Text fontSize="xs" color="gray.500">Items</Text>
                <Text fontSize="xl" fontWeight="700">{totalItems}</Text>
              </Box>
            </HStack>
          </Flex>


          {loading ? (
            <Flex justify="center" py={20}>
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : (
            <Stack spacing={4}>
              {orders.length === 0 ? (
                <Box bg="white" borderRadius="20px" p={8} boxShadow="0 8px 20px rgba(15, 23, 42, 0.06)">
                  <Text fontSize="lg" fontWeight="700" color="#0f172a">
                    No orders found
                  </Text>
                  <Text color="gray.600" mt={2}>
                    Try another filter combination to view more pharmacist orders.
                  </Text>
                </Box>
              ) : (
                orders.map((order) => {
                  const isExpanded = expandedOrderId === order._id;
                  const paymentPlan = getPaymentPlan(order);
                  return (
                    <Box
                      key={order._id}
                      bg="white"
                      borderRadius="20px"
                      p={{ base: 4, md: 5 }}
                      boxShadow="0 10px 30px rgba(15, 23, 42, 0.08)"
                      border="1px solid #e2e8f0"
                    >
                      {isProviderDown(order) && (
                        <Box bg="#fef2f2" border="1px solid #fca5a5" borderRadius="8px" p={3} mb={4}>
                          <Flex align="center" gap={2}>
                            <Icon as={FiActivity} color="#dc2626" boxSize={5} flexShrink={0} />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="700" color="#dc2626" m={0}>
                                {order?.pharmacy?.inventory?.type === 'easysol' ? 'EasySol' : 'Inventory Provider'} is currently unavailable
                              </Text>
                              <Text fontSize="xs" color="#991b1b" m={0}>
                                Cannot process orders until the provider is back online. Please contact support if this persists.
                              </Text>
                            </VStack>
                          </Flex>
                        </Box>
                      )}
                      <Flex justify="space-between" align="center" direction={{ base: "column", md: "row" }} gap={3} minH="60px">
                        <VStack align="start" spacing={0} minW="140px" justify="center" h="100%">
                          <Text fontSize="sm" color="gray.500" lineHeight="1.2">
                            Order #{String(order._id).slice(-8)}
                          </Text>
                          <Text fontSize="md" fontWeight="700" color="#0f172a" lineHeight="1.2">
                            {order.patient?.name || "Unknown patient"}
                          </Text>
                        </VStack>

                        {(order.address || order.deliveryLocation?.address) && (
                          <Flex align="center" justify="center" flex={1} minW={0} h="100%">
                            <HStack spacing={2} fontSize="sm" color="gray.600" h="100%" align="center">
                              <Icon as={FiTruck} boxSize={3.5} color="#0f766e" flexShrink={0} />
                              <Text noOfLines={1} m={0}>
                                {order.address || order.deliveryLocation?.address}
                                {(order.distance || order.deliveryLocation?.distance) && ` · ${order.distance || order.deliveryLocation?.distance} km`}
                              </Text>
                            </HStack>
                          </Flex>
                        )}

                        <HStack spacing={3} wrap="wrap" justify={{ base: "flex-start", md: "flex-end" }} align="center" h="100%">
                          <Badge colorScheme={statusColorMap[order.status] || "gray"} px={3} py={1} borderRadius="full" height="fit-content">
                            {formatStatus(order.status)}
                          </Badge>
                          {order.easysolPosting?.invoiceNo && (
                            <Badge colorScheme="purple" px={3} py={1} borderRadius="full" height="fit-content">
                              Invoice: {order.easysolPosting.invoiceNo}
                            </Badge>
                          )}
                          {order.paymentWorkflow?.captureStatus && (
                            <Badge
                              colorScheme={
                                order.paymentWorkflow.captureStatus === 'captured' ? 'green'
                                : order.paymentWorkflow.captureStatus === 'authorized' ? 'yellow'
                                : order.paymentWorkflow.captureStatus === 'voided' ? 'gray'
                                : order.paymentWorkflow.captureStatus === 'refunded' ? 'red'
                                : 'gray'
                              }
                              px={3} py={1} borderRadius="full" height="fit-content"
                            >
                              {order.paymentWorkflow.captureStatus === 'authorized' ? 'Payment held'
                                : order.paymentWorkflow.captureStatus === 'captured' ? 'Payment captured'
                                : order.paymentWorkflow.captureStatus === 'voided' ? 'Payment voided'
                                : order.paymentWorkflow.captureStatus === 'refunded' ? 'Payment refunded'
                                : order.paymentWorkflow.captureStatus}
                            </Badge>
                          )}
                          <VStack spacing={0} align="end">
                            {getFinalTotal(order) !== order.bill && (
                              <Text fontSize="xs" color="gray.500" m={0}>
                                Was: {formatCurrency(order.bill || 0)}
                              </Text>
                            )}
                            <Text fontWeight="700" color={getFinalTotal(order) !== order.bill ? "#0f766e" : "#0f172a"} m={0}>
                              {formatCurrency(getFinalTotal(order))}
                            </Text>
                          </VStack>
                          <Button size="sm" colorScheme="teal" variant={isExpanded ? "outline" : "solid"} onClick={() => setExpandedOrderId(isExpanded ? null : order._id)}>
                            {isExpanded ? "Hide details" : "View details"}
                          </Button>
                        </HStack>
                      </Flex>

                      {isExpanded && (order.status === 'Delivered' || order.status === 'Cancelled') ? (
                        <Stack spacing={4} mt={5} pt={5} borderTop="1px solid #e2e8f0">
                          {(order.items || []).length > 0 && (
                            <Box>
                              <Text fontSize="11px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                                Items ({(order.items || []).length})
                              </Text>
                              <Box overflowX="auto">
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                  <thead>
                                    <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                      <th style={{ padding: "8px", textAlign: "left", fontWeight: "700", color: "#475569" }}>Item</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontWeight: "700", color: "#475569" }}>Qty</th>
                                      <th style={{ padding: "8px", textAlign: "right", fontWeight: "700", color: "#475569" }}>Price</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.items.map((item, idx) => (
                                      <tr key={`del-item-${idx}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                        <td style={{ padding: "8px", color: "#0f172a" }}>{item.name}</td>
                                        <td style={{ padding: "8px", textAlign: "center", color: "#0f172a" }}>{item.quantity}</td>
                                        <td style={{ padding: "8px", textAlign: "right", color: "#0f172a" }}>{formatCurrency(item.price || 0)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </Box>
                            </Box>
                          )}
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                            <Box bg="#f8fafc" borderRadius="8px" p={3}>
                              <Text fontSize="10px" color="gray.500" textTransform="uppercase" m={0}>Total</Text>
                              <Text fontSize="lg" fontWeight="800" color="#0f172a" m={0}>
                                {formatCurrency(getFinalTotal(order))}
                              </Text>
                            </Box>
                            {order.easysolPosting?.invoiceNo && (
                              <Box bg="#f8fafc" borderRadius="8px" p={3}>
                                <Text fontSize="10px" color="gray.500" textTransform="uppercase" m={0}>Invoice</Text>
                                <Text fontSize="sm" fontWeight="700" color="#0f172a" m={0}>{order.easysolPosting.invoiceNo}</Text>
                              </Box>
                            )}
                            <Box bg="#f8fafc" borderRadius="8px" p={3}>
                              <Text fontSize="10px" color="gray.500" textTransform="uppercase" m={0}>Payment</Text>
                              <Text fontSize="sm" fontWeight="700" color="#0f172a" m={0}>{order.payment_method || "—"}</Text>
                              {order.paymentWorkflow?.captureStatus && (
                                <Text fontSize="11px" color="gray.600" m={0} mt={1}>
                                  {order.paymentWorkflow.captureStatus === 'authorized' ? 'Held'
                                    : order.paymentWorkflow.captureStatus === 'captured' ? 'Captured'
                                    : order.paymentWorkflow.captureStatus === 'voided' ? 'Voided'
                                    : order.paymentWorkflow.captureStatus === 'refunded' ? 'Refunded'
                                    : order.paymentWorkflow.captureStatus}
                                </Text>
                              )}
                            </Box>
                          </SimpleGrid>
                          {order.deliveryWorkflow?.borzoOrderId || order.deliveryWorkflow?.provider === 'pharmacy' ? (
                            <Box bg="#ecfdf5" border="1px solid #a7f3d0" borderRadius="8px" p={3}>
                              <Flex justify="space-between" align="center" mb={2}>
                                <Text fontSize="10px" fontWeight="700" color="#0f766e" textTransform="uppercase" letterSpacing="0.06em" m={0}>Delivery</Text>
                                {order.deliveryWorkflow.status && (
                                  <Badge colorScheme={order.deliveryWorkflow.status === 'delivered' ? 'green' : order.deliveryWorkflow.status === 'cancelled' ? 'red' : 'blue'} fontSize="10px" textTransform="capitalize">
                                    {order.deliveryWorkflow.status.replace(/_/g, ' ')}
                                  </Badge>
                                )}
                              </Flex>
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                <Box>
                                  <Text fontSize="11px" color="gray.600" m={0}>Provider</Text>
                                  <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0}>{order.deliveryWorkflow.provider || "—"}</Text>
                                </Box>
                                {order.deliveryWorkflow.borzoOrderId && (
                                  <Box>
                                    <Text fontSize="11px" color="gray.600" m={0}>Borzo Order ID</Text>
                                    <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0}>#{order.deliveryWorkflow.borzoOrderId}</Text>
                                  </Box>
                                )}
                                {order.deliveryWorkflow.courierName && (
                                  <Box>
                                    <Text fontSize="11px" color="gray.600" m={0}>Courier</Text>
                                    <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0}>
                                      {order.deliveryWorkflow.courierName}{order.deliveryWorkflow.courierPhone ? ` · ${order.deliveryWorkflow.courierPhone}` : ""}
                                    </Text>
                                  </Box>
                                )}
                                {order.deliveryWorkflow.trackingUrl && (
                                  <Box>
                                    <Text fontSize="11px" color="gray.600" m={0}>Tracking</Text>
                                    <Link href={order.deliveryWorkflow.trackingUrl} isExternal color="#0f766e" fontSize="13px" fontWeight="700">
                                      View tracking
                                    </Link>
                                  </Box>
                                )}
                                {order.deliveryWorkflow.borzoStatus && (
                                  <Box>
                                    <Text fontSize="11px" color="gray.600" m={0}>Borzo Status</Text>
                                    <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0}>{order.deliveryWorkflow.borzoStatus}</Text>
                                  </Box>
                                )}
                                {order.deliveryWorkflow.statusDescription && (
                                  <Box gridColumn={{ md: "span 2" }}>
                                    <Text fontSize="11px" color="gray.600" m={0}>Status Description</Text>
                                    <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0}>{order.deliveryWorkflow.statusDescription}</Text>
                                  </Box>
                                )}
                                {order.deliveryWorkflow.lastSyncedAt && (
                                  <Box>
                                    <Text fontSize="11px" color="gray.600" m={0}>Last Updated</Text>
                                    <Text fontSize="13px" fontWeight="700" color="#0f172a" m={0}>{new Date(order.deliveryWorkflow.lastSyncedAt).toLocaleString()}</Text>
                                  </Box>
                                )}
                              </SimpleGrid>
                            </Box>
                          ) : null}
                        </Stack>
                      ) : isExpanded ? (
                        <Stack spacing={0} divider={<Divider borderColor="#e2e8f0" />} mt={5} pt={5} borderTop="1px solid #e2e8f0">
                          {/* Medicines */}
                          {(() => {
                            // Get all items including those marked out of stock in fulfillment
                            const fulfillmentLines = order.pharmacistFulfillment?.lines || [];
                            const originalItems = order.items || [];
                            
                            // Build a map of all item keys from both original items and fulfillment lines
                            const allItemKeys = new Set();
                            const itemMap = {};
                            const outOfStockMap = {};
                            
                            // Add original items
                            originalItems.forEach((item, idx) => {
                              const key = getRequestedItemKey(item, idx);
                              allItemKeys.add(key);
                              itemMap[key] = { item, index: idx, fromFulfillment: false };
                            });
                            
                            // Track out-of-stock status from fulfillment lines
                            fulfillmentLines.forEach((line) => {
                              if (line.requestedItemKey && line.isOutOfStock) {
                                outOfStockMap[line.requestedItemKey] = true;
                                // Add out-of-stock items that might not be in original items
                                if (!itemMap[line.requestedItemKey]) {
                                  allItemKeys.add(line.requestedItemKey);
                                  itemMap[line.requestedItemKey] = { 
                                    item: { 
                                      name: line.itemName, 
                                      quantity: line.requestedQty,
                                      price: line.mrpOverride 
                                    }, 
                                    index: Object.keys(itemMap).length,
                                    fromFulfillment: true 
                                  };
                                }
                              }
                            });
                            
                            const displayItems = Array.from(allItemKeys).map(key => ({...itemMap[key], itemKey: key}));
                            
                            return displayItems.length > 0 && (
                            <Box px={5} py={4}>
                              <Flex align="center" gap={3} mb={3}>
                                <Box w="32px" h="32px" borderRadius="8px" bg="#f0fdfa" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                                  <Icon as={FiPackage} color="#0f766e" boxSize={3.5} />
                                </Box>
                                <Text fontSize="11px" fontWeight="700" color="#94a3b8" textTransform="uppercase" letterSpacing="0.06em" m={0}>
                                  {displayItems.length} Medicine{displayItems.length !== 1 ? "s" : ""}
                                </Text>
                                {!isOrderEditable(order) && (
                                  <Box as="span" fontSize="10px" fontWeight="700" color="#92400e" bg="#fef9c3" px={2} py="2px" borderRadius="full" border="1px solid #fde68a">
                                    🔒 Locked — {order.workflowStage === 'pending_user_review' ? 'Sent for review' : order.workflowStage === 'pending_payment' ? 'Awaiting payment' : order.workflowStage === 'confirmed' ? 'Confirmed' : 'Read-only'}
                                  </Box>
                                )}
                              </Flex>
                              <Box overflowX="auto">
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                  <thead>
                                    <tr style={{ backgroundColor: "#f0fdfa", borderBottom: "2px solid #0f766e" }}>
                                      <th style={{ padding: "8px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Item Name</th>
                                      <th style={{ padding: "8px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Local Medicine</th>
                                      <th style={{ padding: "8px", textAlign: "left", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Batch</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Qty</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Original Price</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>MRP</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Disc %</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Price</th>
                                      <th style={{ padding: "8px", textAlign: "center", fontSize: "11px", fontWeight: "700", color: "#0f766e" }}>Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {displayItems.map(({ item, index, itemKey: keyFromMap }, displayIdx) => {
                                      const itemKey = keyFromMap || getRequestedItemKey(item, index);
                                      const scopedKey = getScopedKey(order._id, itemKey); // Scoped to this order to prevent cross-order bleed
                                      const lineDraft = getLineDraft(order, itemKey);
                                      // Check both draft and fulfillment data for out of stock status
                                      const isOutOfStock = lineDraft?.isOutOfStock || outOfStockMap[itemKey] || false;
                                      const finalPrice = (lineDraft?.mrpOverride ?? item.price ?? 0) * (1 - (lineDraft?.discountPercent || 0) / 100);

                                      return (
                                        <tr
                                          key={`${order._id}-${item.productId || index}`}
                                          style={{
                                            backgroundColor: isOutOfStock ? "#fee2e2" : "white",
                                            opacity: isOutOfStock ? 0.6 : 1,
                                            borderBottom: "1px solid #e2e8f0",
                                          }}
                                        >
                                          <td style={{ padding: "10px 8px" }}>
                                            <Box>
                                              <Text fontSize="12px" fontWeight="700" color="#334155" m={0}>
                                                {item.name}
                                              </Text>
                                              {(item.manufacturer || item.company) && (
                                                <Text fontSize="11px" color="#64748b" m={0}>
                                                  {item.manufacturer || item.company}
                                                </Text>
                                              )}
                                              {(item.composition || item.salt) && (
                                                <Text fontSize="11px" color="#94a3b8" m={0} noOfLines={1}>
                                                  {item.composition || item.salt}
                                                </Text>
                                              )}
                                              {(item.pack_size || item.packSize || item.strength) && (
                                                <Text fontSize="11px" color="#94a3b8" m={0}>
                                                  {item.pack_size || item.packSize || item.strength}
                                                </Text>
                                              )}
                                            </Box>
                                          </td>
                                          <td style={{ padding: "10px 8px" }}>
                                            {isPharmacyConnected(order) ? (
                                              <Box position="relative">
                                                <Input
                                                  size="sm"
                                                  placeholder={selectedMedicineNameByItem[scopedKey] || "Search medicine..."}
                                                  value={selectedMedicineNameByItem[scopedKey] || ""}
                                                  isDisabled={isOutOfStock || isProviderDown(order) || !isOrderEditable(order)}
                                                  fontSize="12px"
                                                  ref={(el) => { if (el) inputRefs.current[itemKey] = el; }}
                                                  onChange={(e) => {
                                                    setSelectedMedicineNameByItem((prev) => ({ ...prev, [scopedKey]: e.target.value }));
                                                    setActiveDropdownItemKey(itemKey);
                                                    searchMedicines(itemKey, e.target.value, inputRefs.current[itemKey], order);
                                                  }}
                                                  onFocus={(e) => {
                                                    setActiveDropdownItemKey(itemKey);
                                                    if (selectedMedicineNameByItem[scopedKey]?.length >= 2) {
                                                      searchMedicines(itemKey, selectedMedicineNameByItem[scopedKey], inputRefs.current[itemKey], order);
                                                    }
                                                  }}
                                                  onBlur={() => {
                                                    dropdownCloseTimer.current = setTimeout(() => {
                                                      setMedicineSearchResultsByItem((prev) => ({ ...prev, [itemKey]: [] }));
                                                      setDropdownPosition(null);
                                                      setActiveDropdownItemKey(null);
                                                    }, 200);
                                                  }}
                                                  autoComplete="off"
                                                />
                                                {medicineSearchingByItem[itemKey] && (
                                                  <Box position="absolute" right="8px" top="50%" transform="translateY(-50%)">
                                                    <Spinner size="xs" color="#0f766e" />
                                                  </Box>
                                                )}
                                                {(medicineSearchResultsByItem[itemKey]?.length || 0) > 0 && dropdownPosition && activeDropdownItemKey === itemKey && (
                                                  <Box
                                                    position="fixed"
                                                    top={`${dropdownPosition.top}px`}
                                                    left={`${dropdownPosition.left}px`}
                                                    width={`${dropdownPosition.width}px`}
                                                    bg="white"
                                                    border="2px solid #0f766e"
                                                    borderRadius="8px"
                                                    zIndex={9999}
                                                    maxH="300px"
                                                    overflowY="auto"
                                                    boxShadow="0 12px 32px rgba(0,0,0,0.25)"
                                                    onMouseDown={(e) => { e.preventDefault(); clearTimeout(dropdownCloseTimer.current); }}
                                                  >
                                                    {medicineSearchResultsByItem[itemKey].map((medicine, idx) => (
                                                      <Box
                                                        key={`${itemKey}-${medicine.item_code}-${idx}`}
                                                        px={3}
                                                        py={2}
                                                        _hover={{ bg: "#f0fdfa" }}
                                                        cursor="pointer"
                                                        borderBottom={idx < medicineSearchResultsByItem[itemKey].length - 1 ? "1px solid #e2e8f0" : "none"}
                                                        onClick={() => selectMedicine(order._id, itemKey, medicine, order)}
                                                      >
                                                        <Flex justify="space-between" align="flex-start">
                                                          <Box flex={1}>
                                                            <Text fontWeight="600" fontSize="12px" color="#0f172a" m={0} noOfLines={1}>
                                                              {medicine.item_name}
                                                            </Text>
                                                            <Text fontSize="10px" color="#64748b" m={0}>
                                                              {medicine.manufacturer} • {medicine.pack_size}
                                                            </Text>
                                                            {medicine.salt && (
                                                              <Text fontSize="9px" color="#94a3b8" m={0} noOfLines={1}>
                                                                {medicine.salt}
                                                              </Text>
                                                            )}
                                                          </Box>
                                                          <VStack align="flex-end" spacing={0} ml={2}>
                                                            <Text fontSize="11px" color="#0f766e" fontWeight="700" m={0}>
                                                              {formatCurrency(medicine.mrp)}
                                                            </Text>
                                                            {medicine.stock_qty > 0 && (
                                                              <Badge size="xs" colorScheme="green" fontSize="9px">
                                                                Stock: {medicine.stock_qty}
                                                              </Badge>
                                                            )}
                                                          </VStack>
                                                        </Flex>
                                                      </Box>
                                                    ))}
                                                  </Box>
                                                )}
                                                {/* Medicine selected confirmation - positioned at medicine input */}
                                                {selectedMedicineNameByItem[scopedKey] && !medicineSearchResultsByItem[itemKey]?.length && !medicineSearchingByItem[itemKey] && dropdownPosition && activeDropdownItemKey === itemKey && (
                                                  <Box
                                                    position="fixed"
                                                    top={`${dropdownPosition.top}px`}
                                                    left={`${dropdownPosition.left}px`}
                                                    width={`${dropdownPosition.width}px`}
                                                    bg="#f0fdfa"
                                                    border="1px solid #0f766e"
                                                    borderRadius="6px"
                                                    zIndex={9998}
                                                    p={2}
                                                  >
                                                    <Text fontSize="11px" color="#0f766e" textAlign="center" m={0}>
                                                      ✓ Medicine selected: {selectedMedicineNameByItem[scopedKey]}
                                                    </Text>
                                                  </Box>
                                                )}
                                                {/* Only show "No medicines found" when user has typed but hasn't selected anything yet */}
                                                {selectedMedicineNameByItem[scopedKey]?.length >= 2 &&
                                                  medicineSearchResultsByItem[itemKey]?.length === 0 &&
                                                  !medicineSearchingByItem[itemKey] &&
                                                  !medicineBatchesByItem[itemKey] &&
                                                  dropdownPosition && activeDropdownItemKey === itemKey && (
                                                  <Box
                                                    position="fixed"
                                                    top={`${dropdownPosition.top}px`}
                                                    left={`${dropdownPosition.left}px`}
                                                    width={`${dropdownPosition.width}px`}
                                                    bg="#fef2f2"
                                                    border="1px solid #fca5a5"
                                                    borderRadius="6px"
                                                    zIndex={9999}
                                                    p={2}
                                                  >
                                                    <Text fontSize="11px" color="#dc2626" textAlign="center" m={0}>
                                                      No medicines found
                                                    </Text>
                                                  </Box>
                                                )}
                                                {/* Show selected medicine manufacturer and salt below input */}
                                                {selectedMedicineDetailsByItem[scopedKey] && (
                                                  <Box position="absolute" top="100%" left="0" right="0" pt="2px">
                                                    <Text fontSize="9px" color="#64748b" noOfLines={1} bg="white">
                                                      <Text as="span" fontWeight="600">{selectedMedicineDetailsByItem[scopedKey].manufacturer}</Text>
                                                      {selectedMedicineDetailsByItem[scopedKey].salt && (
                                                        <Text as="span"> • {selectedMedicineDetailsByItem[scopedKey].salt}</Text>
                                                      )}
                                                    </Text>
                                                  </Box>
                                                )}
                                              </Box>
                                            ) : (
                                              <Input
                                                size="sm"
                                                placeholder="Local medicine name"
                                                value={selectedMedicineNameByItem[scopedKey] || ""}
                                                isDisabled={isOutOfStock || isProviderDown(order) || !isOrderEditable(order)}
                                                fontSize="12px"
                                                onChange={(e) => {
                                                  const value = e.target.value;
                                                  setSelectedMedicineNameByItem((prev) => ({
                                                    ...prev,
                                                    [scopedKey]: value,
                                                  }));
                                                  setOrderDraft(order._id, (current) => {
                                                    let line = current.lines?.[itemKey];
                                                    if (!line && order?.items) {
                                                      const item = order.items.find((i, idx) => getRequestedItemKey(i, idx) === itemKey);
                                                      line = {
                                                        requestedItemKey: itemKey,
                                                        itemCode: item?.itemCode || "",
                                                        itemName: item?.name || "",
                                                        requestedQty: Number(item?.quantity || 1),
                                                        discountPercent: 0,
                                                        mrpOverride: Number(item?.price || 0),
                                                        allocations: [],
                                                      };
                                                    }
                                                    if (!line) return current;
                                                    return {
                                                      ...current,
                                                      lines: {
                                                        ...(current.lines || {}),
                                                        [itemKey]: {
                                                          ...line,
                                                          itemName: value,
                                                        },
                                                      },
                                                    };
                                                  });
                                                }}
                                              />
                                            )}
                                          </td>
                                          <td style={{ padding: "10px 8px" }}>
                                            <Box position="relative">
                                              <Input
                                                size="sm"
                                                placeholder={selectedBatchByItem[scopedKey]?.batch_no || "Select batch..."}
                                                isDisabled={isOutOfStock || isProviderDown(order) || !isOrderEditable(order)}
                                                fontSize="12px"
                                                ref={(el) => { if (el) batchInputRefs.current[itemKey] = el; }}
                                                onClick={() => {
                                                  if (medicineBatchesByItem[itemKey]?.length > 0 && batchInputRefs.current[itemKey]) {
                                                    const rect = batchInputRefs.current[itemKey].getBoundingClientRect();
                                                    setBatchDropdownPositionByItem((prev) => ({
                                                      ...prev,
                                                      [itemKey]: {
                                                        top: rect.bottom + window.scrollY,
                                                        left: rect.left + window.scrollX,
                                                        width: Math.max(rect.width, 280),
                                                      },
                                                    }));
                                                    // Clear selected batch to allow re-selection
                                                    setSelectedBatchByItem((prev) => ({
                                                      ...prev,
                                                      [scopedKey]: null,
                                                    }));
                                                  }
                                                }}
                                                onFocus={() => {
                                                  // Also open dropdown on focus (tab navigation)
                                                  if (medicineBatchesByItem[itemKey]?.length > 0 && batchInputRefs.current[itemKey]) {
                                                    const rect = batchInputRefs.current[itemKey].getBoundingClientRect();
                                                    setBatchDropdownPositionByItem((prev) => ({
                                                      ...prev,
                                                      [itemKey]: {
                                                        top: rect.bottom + window.scrollY,
                                                        left: rect.left + window.scrollX,
                                                        width: Math.max(rect.width, 280),
                                                      },
                                                    }));
                                                    setSelectedBatchByItem((prev) => ({
                                                      ...prev,
                                                      [scopedKey]: null,
                                                    }));
                                                  }
                                                }}
                                                cursor={selectedMedicineNameByItem[scopedKey] ? "pointer" : "not-allowed"}
                                                onChange={(e) => {
                                                  if (!isPharmacyConnected(order)) {
                                                    setManualBatchByItem((prev) => ({
                                                      ...prev,
                                                      [scopedKey]: e.target.value,
                                                    }));
                                                    // Update order draft with manual batch
                                                    setOrderDraft(order._id, (current) => {
                                                      let line = current.lines?.[itemKey];
                                                      if (!line && order?.items) {
                                                        const item = order.items.find((i, idx) => getRequestedItemKey(i, idx) === itemKey);
                                                        line = {
                                                          requestedItemKey: itemKey,
                                                          itemCode: item?.itemCode || "",
                                                          itemName: item?.name || "",
                                                          requestedQty: Number(item?.quantity || 1),
                                                          discountPercent: 0,
                                                          mrpOverride: Number(item?.price || 0),
                                                          allocations: [],
                                                        };
                                                      }
                                                      if (!line) return current;
                                                      return {
                                                        ...current,
                                                        lines: {
                                                          ...(current.lines || {}),
                                                          [itemKey]: {
                                                            ...line,
                                                            batchNo: e.target.value,
                                                          },
                                                        },
                                                      };
                                                    });
                                                  }
                                                }}
                                                value={selectedBatchByItem[scopedKey]?.batch_no || manualBatchByItem[scopedKey] || ""}
                                              />
                                              {(medicineBatchesByItem[itemKey]?.length || 0) > 0 && batchDropdownPositionByItem[itemKey] && (
                                                <Box
                                                  position="fixed"
                                                  top={`${batchDropdownPositionByItem[itemKey].top}px`}
                                                  left={`${batchDropdownPositionByItem[itemKey].left}px`}
                                                  width={`${batchDropdownPositionByItem[itemKey].width}px`}
                                                  bg="white"
                                                  border="2px solid #0f766e"
                                                  borderRadius="8px"
                                                  zIndex={9999}
                                                  maxH="250px"
                                                  overflowY="auto"
                                                  boxShadow="0 12px 32px rgba(0,0,0,0.25)"
                                                  style={{
                                                    transform: 'translateY(4px)',
                                                  }}
                                                >
                                                  {/* Show message if no batches have sufficient quantity */}
                                                  <>
                                                  {medicineBatchesByItem[itemKey].filter((b) => b.available_qty >= (item.quantity || 1)).length === 0 && (
                                                    <Box px={3} py={2} bg="#fef2f2" borderRadius="6px" m={2}>
                                                      <Text fontSize="11px" color="#dc2626" textAlign="center" m={0}>
                                                        No batches with sufficient quantity
                                                      </Text>
                                                    </Box>
                                                  )}
                                                  {medicineBatchesByItem[itemKey]
                                                    .filter((batch) => batch.available_qty >= (item.quantity || 1))
                                                    .map((batch, idx) => (
                                                    <Box
                                                      key={`${itemKey}-batch-${batch.batch_no}-${idx}`}
                                                      px={3}
                                                      py={2}
                                                      _hover={{ bg: "#f0fdfa" }}
                                                      cursor="pointer"
                                                      borderBottom={idx < medicineBatchesByItem[itemKey].length - 1 ? "1px solid #e2e8f0" : "none"}
                                                      onClick={() => selectBatch(order._id, itemKey, batch, order)}
                                                    >
                                                      <Flex justify="space-between" align="center">
                                                        <VStack align="flex-start" spacing={0}>
                                                          <Text fontWeight="600" fontSize="12px" color="#0f172a" m={0}>
                                                            Batch: {batch.batch_no}
                                                          </Text>
                                                          <Text fontSize="10px" color="#64748b" m={0}>
                                                            Exp: {batch.expiry || "N/A"}
                                                          </Text>
                                                        </VStack>
                                                        <VStack align="flex-end" spacing={0}>
                                                          <Text fontSize="11px" color="#0f766e" fontWeight="700" m={0}>
                                                            {formatCurrency(batch.sale_rate || batch.mrp)}
                                                          </Text>
                                                          <Badge size="xs" colorScheme={batch.available_qty > 0 ? "green" : "red"} fontSize="9px">
                                                            Qty: {batch.available_qty}
                                                          </Badge>
                                                        </VStack>
                                                      </Flex>
                                                    </Box>
                                                  ))}
                                                  </>
                                                </Box>
                                              )}
                                            </Box>
                                          </td>
                                          <td style={{ padding: "10px 8px", textAlign: "center" }}>
                                            <Text fontSize="12px" fontWeight="600" m={0}>
                                              {item.quantity || 1}
                                            </Text>
                                          </td>
                                          <td style={{ padding: "10px 8px", textAlign: "center" }}>
                                            <Text fontSize="12px" fontWeight="600" color="#334155" m={0}>
                                              {formatCurrency(item.price || 0)}
                                            </Text>
                                          </td>
                                          <td style={{ padding: "10px 8px" }}>
                                            <Input
                                              size="sm"
                                              type="number"
                                              placeholder="MRP"
                                              value={lineDraft?.mrpOverride || item.price || ""}
                                              onChange={(e) => updateLineMrpOverride(order._id, itemKey, e.target.value, order)}
                                              isDisabled={isOutOfStock || !isOrderEditable(order)}
                                              fontSize="12px"
                                              min="0"
                                            />
                                          </td>
                                          <td style={{ padding: "10px 8px" }}>
                                            <Input
                                              size="sm"
                                              type="number"
                                              placeholder="%"
                                              value={lineDraft?.discountPercent || 0}
                                              onChange={(e) => updateLineDiscount(order._id, itemKey, e.target.value, order)}
                                              isDisabled={isOutOfStock || !isOrderEditable(order)}
                                              fontSize="12px"
                                              min="0"
                                              max="100"
                                            />
                                          </td>
                                          <td style={{ padding: "10px 8px", textAlign: "center" }}>
                                            <Box bg={isOutOfStock ? "transparent" : "white"} borderRadius="6px" px={2} py={1}>
                                              <Text fontSize="12px" fontWeight="700" color="#0f766e" m={0}>
                                                {formatCurrency(finalPrice * (item.quantity || 1))}
                                              </Text>
                                            </Box>
                                          </td>
                                          <td style={{ padding: "10px 8px", textAlign: "center" }}>
                                            <Button
                                              size="xs"
                                              colorScheme={isOutOfStock ? "red" : "gray"}
                                              variant={isOutOfStock ? "solid" : "outline"}
                                              onClick={() => toggleLineOutOfStock(order._id, itemKey)}
                                              fontSize="11px"
                                              isDisabled={!isOrderEditable(order)}
                                            >
                                              {isOutOfStock ? "In" : "Out"}
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </Box>
                            </Box>
                            );
                          })()}

                          {/* Prescription Files */}
                          {(order.prescriptionFiles || []).length > 0 && (
                            <Box px={5} pb={3}>
                              <Box border="1px solid #e2e8f0" borderRadius="12px" overflow="hidden">
                                <Flex align="center" gap={2} px={4} py={2.5} bg="#f8fafc" borderBottom="1px solid #e2e8f0">
                                  <Icon as={FiFileText} color="#0f766e" boxSize={3.5} />
                                  <Text fontSize="11px" fontWeight="700" color="#0f766e" textTransform="uppercase" letterSpacing="0.06em" m={0}>
                                    Prescription {order.prescriptionFiles.length > 1 ? `(${order.prescriptionFiles.length} files)` : ""}
                                  </Text>
                                </Flex>
                                <Stack spacing={0} divider={<Box h="1px" bg="#f1f5f9" />}>
                                  {order.prescriptionFiles.map((f, fi) => {
                                    const url = getFileUrl(f.path);
                                    const isImg = isImageFile(f.mimeType);
                                    return (
                                      <Flex key={fi} align="center" gap={3} px={4} py={2.5}>
                                        {isImg ? (
                                          <Box
                                            as="img"
                                            src={url}
                                            alt={f.originalName}
                                            w="40px" h="40px"
                                            objectFit="cover"
                                            borderRadius="6px"
                                            border="1px solid #e2e8f0"
                                            flexShrink={0}
                                            cursor="pointer"
                                            onClick={() => setPreviewFile({ url, originalName: f.originalName, fileName: f.fileName })}
                                          />
                                        ) : (
                                          <Flex w="40px" h="40px" borderRadius="6px" bg="#f0fdfa" align="center" justify="center" flexShrink={0} border="1px solid #ccfbf1">
                                            <Icon as={FiFile} color="#0f766e" boxSize={4} />
                                          </Flex>
                                        )}
                                        <Box flex={1} minW={0}>
                                          <Text fontSize="12px" fontWeight="600" color="#0f172a" noOfLines={1} m={0}>{f.originalName || f.fileName || `File ${fi + 1}`}</Text>
                                          {f.size && <Text fontSize="10px" color="#94a3b8" m={0}>{(f.size / 1024).toFixed(0)} KB</Text>}
                                        </Box>
                                        <Button
                                          as="a" href={url} target="_blank" rel="noopener noreferrer"
                                          size="xs" variant="outline" colorScheme="teal" fontWeight="700" flexShrink={0}
                                        >
                                          Open
                                        </Button>
                                        {isImg && (
                                          <Button
                                            size="xs" variant="ghost" colorScheme="teal" fontWeight="700" flexShrink={0}
                                            onClick={() => setPreviewFile({ url, originalName: f.originalName, fileName: f.fileName })}
                                          >
                                            Preview
                                          </Button>
                                        )}
                                      </Flex>
                                    );
                                  })}
                                </Stack>
                              </Box>
                            </Box>
                          )}

                          {/* Order Summary - 4 Column Layout */}
                          {(order.items || []).length > 0 && (
                            <Box px={5} py={4}>
                              <Box bg="#f0fdfa" borderRadius="12px" p={4} border="1px solid #0f766e">
                                <Text fontSize="12px" fontWeight="600" color="#0f766e" mb={3}>Order Summary</Text>

                                <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
                                  {/* Column 1: Pricing */}
                                  <VStack spacing={2} align="flex-start" borderRight={{ md: "1px solid #cbd5e1" }} pr={{ md: 3 }}>
                                    <Box>
                                      <Text fontSize="10px" color="gray.500" mb={1}>Original Amount</Text>
                                      <Text fontSize="12px" fontWeight="700" color="#334155">
                                        {formatCurrency(order.bill || 0)}
                                      </Text>
                                    </Box>
                                    <Box>
                                      <Text fontSize="10px" color="gray.500" mb={1}>In Store Price</Text>
                                      <Text fontSize="12px" fontWeight="700" color="#0f766e">
                                        {formatCurrency(
                                          Object.values(getOrderDraft(order).lines || {})
                                            .filter((line) => !line.isOutOfStock)
                                            .reduce((sum, line) => sum + ((line.mrpOverride || 0) * (line.requestedQty || 0)), 0)
                                        )}
                                      </Text>
                                    </Box>
                                    <Box>
                                      <Text fontSize="10px" color="gray.500" mb={1}>Delivery Fee</Text>
                                      <Text fontSize="12px" fontWeight="700" color="#0f766e">
                                        {formatCurrency(getDeliveryFee(order))}
                                      </Text>
                                    </Box>
                                    <Flex gap={4} width="100%">
                                      <Box>
                                        <Text fontSize="10px" color="gray.500" mb={1}>Medicines</Text>
                                        <Text fontSize="12px" fontWeight="700" color="#0f172a">
                                          {Object.values(getOrderDraft(order).lines || {}).filter((line) => !line.isOutOfStock).length}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="10px" color="gray.500" mb={1}>Total Items</Text>
                                        <Text fontSize="12px" fontWeight="700" color="#0f172a">
                                          {Object.values(getOrderDraft(order).lines || {})
                                            .filter((line) => !line.isOutOfStock)
                                            .reduce((sum, line) => sum + (line.requestedQty || 0), 0)}
                                        </Text>
                                      </Box>
                                    </Flex>
                                  </VStack>

                                  {/* Column 2: Discounts */}
                                  <VStack spacing={2} align="flex-start" borderRight={{ md: "1px solid #cbd5e1" }} pr={{ md: 3 }}>
                                    {getOrderTotals(order).lineDiscountTotal > 0 && (
                                      <Box width="100%">
                                        <Text fontSize="10px" color="gray.500" mb={1}>Line Discounts</Text>
                                        <Text fontSize="12px" fontWeight="700" color="#dc2626">
                                          -{formatCurrency(getOrderTotals(order).lineDiscountTotal)}
                                        </Text>
                                      </Box>
                                    )}
                                    <Box width="100%">
                                      <Text fontSize="10px" color="gray.500" mb={1}>Order Discount %</Text>
                                      <Input
                                        type="number"
                                        size="sm"
                                        value={getOrderDraft(order).grossDiscountPercent || 0}
                                        onChange={(e) => updateGrossDiscount(order._id, e.target.value)}
                                        min="0"
                                        max="100"
                                        placeholder="0"
                                      />
                                    </Box>
                                    {getOrderTotals(order).grossDiscountAmount > 0 && (
                                      <Box width="100%">
                                        <Text fontSize="10px" color="gray.500">Discount Amount</Text>
                                        <Text fontSize="11px" fontWeight="700" color="#dc2626">
                                          -{formatCurrency(getOrderTotals(order).grossDiscountAmount)}
                                        </Text>
                                      </Box>
                                    )}
                                  </VStack>

                                  {/* Column 3: Delivery Options */}
                                  <VStack spacing={2} align="flex-start" borderRight={{ md: "1px solid #cbd5e1" }} pr={{ md: 3 }}>
                                    {order.deliveryWorkflow?.availableOptions?.length > 0 && !order.deliveryWorkflow?.borzoOrderId && (
                                      <>
                                        <Text fontSize="10px" fontWeight="600" color="#0f766e">Delivery Options</Text>
                                        <VStack spacing={1} width="100%">
                                          {order.deliveryWorkflow.availableOptions.map((option, idx) => (
                                            <Box
                                              key={`delopt-${idx}`}
                                              width="100%"
                                              p={1.5}
                                              borderRadius="4px"
                                              border={order.deliveryWorkflow?.selectedOption?.id === option.id ? "1.5px solid #0f766e" : "1px solid #cbd5e1"}
                                              bg={order.deliveryWorkflow?.selectedOption?.id === option.id ? "#ecfdf5" : "white"}
                                              cursor="pointer"
                                              onClick={() => {
                                                setOrders((prev) =>
                                                  prev.map((entry) =>
                                                    entry._id === order._id
                                                      ? {
                                                          ...entry,
                                                          deliveryWorkflow: {
                                                            ...(entry.deliveryWorkflow || {}),
                                                            selectedOption: option,
                                                          },
                                                        }
                                                      : entry
                                                  )
                                                );
                                              }}
                                              transition="all 0.2s"
                                              _hover={{ borderColor: "#0f766e" }}
                                            >
                                              <Flex justify="space-between" align="flex-start" gap={1}>
                                                <VStack align="flex-start" spacing={0} flex={1}>
                                                  <Text fontSize="9px" fontWeight="600" color="#0f172a" lineHeight="1">
                                                    {option.label}
                                                  </Text>
                                                  {option.etaLabel && (
                                                    <Text fontSize="8px" color="gray.600">
                                                      {option.etaLabel}
                                                    </Text>
                                                  )}
                                                </VStack>
                                                <Text fontSize="9px" fontWeight="700" color="#0f766e" whiteSpace="nowrap">
                                                  {formatCurrency(option.deliveryFee || 0)}
                                                </Text>
                                              </Flex>
                                            </Box>
                                          ))}
                                        </VStack>
                                      </>
                                    )}
                                    {order.deliveryWorkflow?.selectedOption && !order.deliveryWorkflow?.borzoOrderId && (
                                      <Box width="100%" bg="white" p={2} borderRadius="4px" border="1px solid #0f766e">
                                        <Text fontSize="9px" color="gray.600" mb={1}>Selected:</Text>
                                        <Text fontSize="9px" fontWeight="600" color="#0f172a">
                                          {order.deliveryWorkflow.selectedOption.label}
                                        </Text>
                                        <Text fontSize="9px" fontWeight="700" color="#0f766e" mt={1}>
                                          {formatCurrency(order.deliveryWorkflow.selectedOption.deliveryFee || 0)}
                                        </Text>
                                      </Box>
                                    )}
                                    {order.deliveryWorkflow?.borzoOrderId && (
                                      <Box width="100%" bg="#ecfdf5" p={2} borderRadius="4px" border="1px solid #0f766e">
                                        <Text fontSize="9px" color="gray.600" mb={1}>Borzo Order:</Text>
                                        <Text fontSize="9px" fontWeight="700" color="#0f172a" mb={1}>
                                          #{order.deliveryWorkflow.borzoOrderId}
                                        </Text>
                                        {(order.deliveryWorkflow.borzoStatus || order.deliveryWorkflow.status) && (
                                          <Text fontSize="9px" color="gray.600" mb={1}>
                                            Status: <Text as="span" fontWeight="700" color="#0f172a">
                                              {(order.deliveryWorkflow.borzoStatus || order.deliveryWorkflow.status || '')
                                                .replace(/_/g, ' ')
                                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                                            </Text>
                                          </Text>
                                        )}
                                        {order.deliveryWorkflow.courierName && (
                                          <Text fontSize="9px" color="gray.600" mb={1}>
                                            Courier: <Text as="span" fontWeight="700" color="#0f172a">{order.deliveryWorkflow.courierName}</Text>
                                          </Text>
                                        )}
                                        {order.deliveryWorkflow.courierPhone && (
                                          <Text fontSize="9px" color="gray.600" mb={1}>
                                            Phone: <Text as="span" fontWeight="700" color="#0f172a">{order.deliveryWorkflow.courierPhone}</Text>
                                          </Text>
                                        )}
                                        {order.deliveryWorkflow.trackingUrl && (
                                          <Button
                                            as="a"
                                            href={order.deliveryWorkflow.trackingUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="xs"
                                            colorScheme="teal"
                                            variant="outline"
                                            width="100%"
                                            mt={1}
                                            leftIcon={<Icon as={FiTruck} boxSize={2.5} />}
                                          >
                                            Track Live
                                          </Button>
                                        )}
                                        <Button
                                          size="xs"
                                          colorScheme="blue"
                                          variant="outline"
                                          width="100%"
                                          mt={1}
                                          leftIcon={<Icon as={FiRefreshCw} boxSize={2.5} />}
                                          isLoading={Boolean(deliveryRefreshByOrder[order._id])}
                                          onClick={() => refreshDeliveryStatus(order)}
                                        >
                                          Refresh Status
                                        </Button>
                                      </Box>
                                    )}
                                    {/* Final Total moved to Delivery section */}
                                    <Box width="100%" bg="#ecfdf5" borderRadius="6px" p={2} mt={2}>
                                      <Text fontSize="9px" color="gray.600" mb={1}>Final Total</Text>
                                      <Text fontSize="14px" fontWeight="800" color="#0f766e">
                                        {formatCurrency(getFinalTotal(order))}
                                      </Text>
                                    </Box>
                                  </VStack>

                                  {/* Column 4: Action Buttons */}
                                  <VStack spacing={2} align="flex-start" justify="flex-start" width="100%">
                                      {/* Status label when awaiting customer */}
                                      {(order.workflowStage === 'pending_user_review' || order.workflowStage === 'pending_payment') && (
                                        <Box width="100%" bg={order.workflowStage === 'pending_user_review' ? '#fef9c3' : '#eff6ff'} borderRadius="8px" px={3} py={2} border={`1px solid ${order.workflowStage === 'pending_user_review' ? '#fde68a' : '#bfdbfe'}`}>
                                          <Text fontSize="11px" fontWeight="700" color={order.workflowStage === 'pending_user_review' ? '#92400e' : '#1e40af'} m={0} textAlign="center">
                                            {order.workflowStage === 'pending_user_review' ? '⏳ Awaiting customer review' : '💳 Awaiting customer payment'}
                                          </Text>
                                        </Box>
                                      )}
                                      {/* Send for review — pharmacist sends verified order to customer */}
                                      <Button
                                        width="100%"
                                        colorScheme="orange"
                                        variant="outline"
                                        size="sm"
                                        fontWeight="700"
                                        isLoading={Boolean(proposingByOrder[order._id])}
                                        isDisabled={isProviderDown(order) || order.workflowStage !== 'review'}
                                        onClick={() => pharmacistSendForReview(order)}
                                      >
                                        {order.workflowStage === 'pending_user_review' ? '⏳ Sent for review' : 'Send for Review'}
                                      </Button>
                                      <Button
                                        width="100%"
                                        colorScheme="blue"
                                        size="sm"
                                        fontWeight="700"
                                        onClick={() => generateInvoice(order)}
                                        isLoading={Boolean(savingByOrder[order._id])}
                                        isDisabled={isProviderDown(order) || order.status !== 'Confirmed' || order.workflowStage === 'rejected'}
                                      >
                                        {order.status === 'Invoice Generated' ? '✓ Invoice Generated' : 'Generate Invoice'}
                                      </Button>
                                      <Button
                                        width="100%"
                                        colorScheme="red"
                                        size="sm"
                                        fontWeight="700"
                                        isLoading={Boolean(rejectingByOrder[order._id])}
                                        isDisabled={isProviderDown(order) || !['review', 'pending_user_review', 'pending_payment'].includes(order.workflowStage)}
                                        onClick={() => rejectOrder(order)}
                                      >
                                        {order.workflowStage === 'rejected' ? '✗ Rejected' : 'Reject Order'}
                                      </Button>
                                      {order.status === 'Invoice Generated' && !order.deliveryWorkflow?.borzoOrderId && order.deliveryWorkflow?.provider !== 'pharmacy' && (
                                        <Button
                                          width="100%"
                                          colorScheme="green"
                                          size="sm"
                                          fontWeight="700"
                                          onClick={() => createDelivery(order)}
                                          isLoading={Boolean(deliveryCreateByOrder[order._id])}
                                          isDisabled={isProviderDown(order)}
                                        >
                                          Start Delivery
                                        </Button>
                                      )}
                                      {order.deliveryWorkflow?.provider === 'pharmacy' && order.workflowStage === 'delivery_in_progress' && (
                                        <Button
                                          width="100%"
                                          colorScheme="green"
                                          size="sm"
                                          fontWeight="700"
                                          leftIcon={<Icon as={FiCheckCircle} />}
                                          onClick={() => markOrderDelivered(order)}
                                          isLoading={Boolean(markDeliveredByOrder[order._id])}
                                        >
                                          Mark as Delivered
                                        </Button>
                                      )}
                                    </VStack>
                                </Grid>
                              </Box>
                            </Box>
                          )}
                        </Stack>
                      ) : null}
                    </Box>
                  );
                })
              )}
            </Stack>
          )}
        </Stack>
      </Box>
      <Modal isOpen={Boolean(previewFile)} onClose={() => setPreviewFile(null)} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalCloseButton />
          <ModalBody p={4}>
            {previewFile ? (
              <Image
                src={previewFile.url}
                alt={previewFile.originalName || previewFile.fileName || "Prescription preview"}
                w="100%"
                maxH="80vh"
                objectFit="contain"
                borderRadius="12px"
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default PharmacistOrders;
